// Lista de /condominios com o MESMO card de imóvel do tenant (modelo_card): cada
// empreendimento vira um `slide` (utils/empreendimentoCard.ts) e entra no card com a prop
// `modoEmpreendimento` (link para /empreendimento/{id}, rótulo, "A partir de", sem
// favorito/compartilhar/código). Paginação infinita por IntersectionObserver como antes.
import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef } from 'react';
import { TailSpin } from 'react-loader-spinner';
import {
    empreendimentoParaSlide,
    CTA_EMPREENDIMENTO, PRECO_PREFIXO_EMPREENDIMENTO,
    rotuloEmpreendimento
} from '../../../../../utils/empreendimentoCard';
import { hexToRgba, isColorLight, removerAcentos } from '../../../../../utils/FunctionsGlobals';
import { appendCodsiteQuery } from '../../../../../utils/url';
import styles from '../../../../imoveis/components/pieces/list/ListPiecesProperties.module.css';
import condoStyles from './ListPiecesCondominio.module.css';

// ESPELHO de ListPiecesProperties.tsx (dynamic dos 5 cards, ssr:false) — manter em sincronia
const CardApartament = dynamic(() => import('../../../../../components/cards/apartament/CardApartament'), { ssr: false });
const CardApartament1 = dynamic(() => import('../../../../../components/cards/apartament/CardApartament1'), { ssr: false });
const CardApartament3 = dynamic(() => import('../../../../../components/cards/apartament/CardApartament3'), { ssr: false });
const CardApartamentHorizontal = dynamic(() => import('../../../../../components/cards/apartament/CardApartamentHorizontal'), { ssr: false });
// CARD "Foto imersiva" (modelo_card = 4): card 100% foto (aspect 4:5) com overlay
const CardApartamentImersivo = dynamic(() => import('../../../../../components/cards/apartament-imersivo/CardApartamentImersivo'), { ssr: false });

// Ids de empreendimento colidiriam com ids de imóvel e acenderiam "Visualizado"/favorito:
// listas vazias e estáveis (mesma referência entre renders).
const SEM_VISUALIZADOS: Array<any> = [];
const SEM_FAVORITOS: Array<any> = [];
const semSetFavorito = () => {};

// ==== Aurora+Panorama (09/10) ====
// Modelos #7 "Aurora" (topo 10) e #10 "Panorama" (topo 13): /condominios no visual do /imoveis desses
// modelos. As cores são as MESMAS de coresListagemAP/varsListagemAP/estilosSelectModeloAP do
// ListPiecesProperties.tsx (ESPELHO — importar de lá traria a listagem inteira para este bundle): viram
// CSS vars --lst-* na raiz (pages/condominios/index.tsx) e as regras [data-modelo='aurora'|'panorama'] do
// ListPiecesProperties.module.css (filtros, barra, chips, grade, vazio) passam a valer aqui também.
// Grade = o card da home (AuroraCard / PanoramaCard em modoEmpreendimento). Fora dos dois: null.
import { isMobile as celularAP } from 'react-device-detect';
import { MotionConfig } from 'framer-motion';
import type { IAuroraCard } from '../../../../../components/carousel/apartment/CardAurora';
import type { IPanoramaCard } from '../../../../../components/carousel/apartment/CardPanorama';
import { EASE_AURORA, ehModeloAurora } from '../../../../../components/pieces/aurora/aurora';
import { EASE_PANORAMA, ehModeloPanorama, paletaPanorama } from '../../../../../components/pieces/panorama/panorama';
import { hexParaRgba, useMovimentoReduzido } from '../../../../../components/pieces/horizonte/horizonte';
import { ensureReadableTextColor, getContrastRatio } from '../../../../../utils/FunctionsGlobals';
const AuroraCard = dynamic<IAuroraCard>(() => import('../../../../../components/carousel/apartment/CardAurora').then((m) => m.AuroraCard), { ssr: false });
const PanoramaCard = dynamic<IPanoramaCard>(() => import('../../../../../components/carousel/apartment/CardPanorama').then((m) => m.PanoramaCard), { ssr: false });

export type CoresCondominioAP = {
    modelo: 'aurora' | 'panorama';
    fundo: string;
    texto: string;
    acento: string;
    solida: string;
    solidaTexto: string;
    painelTexto: string;
    painelAcento: string;
    raio: number;
};
export const coresCondominioAP = (site: any): CoresCondominioAP | null => {
    const aurora = ehModeloAurora(site);
    if (!aurora && !ehModeloPanorama(site)) return null;
    const fundo = site?.cor5 || '#f6f7f9';
    const texto = ensureReadableTextColor(isColorLight(fundo) ? site?.cor11 : site?.cor13, fundo) || '#1c2230';
    if (aurora) {
        const solida = site?.cor2 || '#1c2230';
        return {
            modelo: 'aurora',
            fundo,
            texto,
            acento: ensureReadableTextColor(site?.cor2, fundo, 3.0, texto, texto) || texto,
            solida,
            solidaTexto: ensureReadableTextColor(isColorLight(solida) ? site?.cor11 : site?.cor13, solida) || '#ffffff',
            painelTexto: '#1c2230',
            painelAcento: ensureReadableTextColor(site?.cor2, '#ffffff', 3.0, '#1c2230', '#1c2230') || '#1c2230',
            raio: 999,
        };
    }
    const p = paletaPanorama(site);
    const acento = getContrastRatio(p.acento, fundo) >= 3 ? p.acento : getContrastRatio(p.acentoEscuro, fundo) >= 3 ? p.acentoEscuro : texto;
    return { modelo: 'panorama', fundo, texto, acento, solida: p.acento, solidaTexto: p.acentoTexto, painelTexto: p.escuro, painelAcento: p.vivo, raio: 7 };
};
/** Grade com o card do PRÓPRIO modelo só quando o tenant usa esse card (Aurora = card 7 · Panorama = card 10). */
export const cardCondominioAP = (site: any, c: CoresCondominioAP | null): boolean =>
    c ? Number(site?.modelo_card) === (c.modelo === 'aurora' ? 7 : 10) : false;
export const varsCondominioAP = (c: CoresCondominioAP | null): React.CSSProperties =>
    c
        ? ({
              color: c.texto,
              '--lst-fundo': c.fundo,
              '--lst-texto': c.texto,
              '--lst-acento': c.acento,
              '--lst-sutil': hexParaRgba(c.texto, 0.12),
              '--lst-sutil-forte': hexParaRgba(c.texto, 0.26),
              '--lst-solida': c.solida,
              '--lst-solida-texto': c.solidaTexto,
              '--lst-painel-texto': c.painelTexto,
              '--lst-painel-acento': c.painelAcento,
              '--lst-painel-sutil': hexParaRgba(c.painelTexto, 0.12),
              '--lst-painel-sutil-forte': hexParaRgba(c.painelTexto, 0.22),
          } as React.CSSProperties)
        : {};
/** react-select do painel de filtros no idioma do modelo (44px, pílula/raio 7, menu branco). */
export const estilosSelectCondominioAP = (c: CoresCondominioAP) => {
    const texto = c.painelTexto;
    const foco = c.painelAcento;
    const pilula = c.raio > 100;
    return {
        control: (base: any, state: any) => ({
            ...base,
            minHeight: 44,
            borderRadius: c.raio,
            background: 'transparent',
            boxShadow: 'none',
            border: `1px solid ${state.isFocused ? foco : hexParaRgba(texto, 0.24)}`,
            padding: pilula ? '0 8px 0 12px' : '0 4px 0 8px',
            cursor: 'pointer',
            transition: 'border-color .2s ease',
            ':hover': { borderColor: state.isFocused ? foco : hexParaRgba(texto, 0.5) },
        }),
        valueContainer: (base: any) => ({ ...base, padding: '2px 4px' }),
        placeholder: (base: any) => ({ ...base, color: hexParaRgba(texto, 0.62), fontSize: 14, fontWeight: 500 }),
        singleValue: (base: any) => ({ ...base, color: texto, fontSize: 14, fontWeight: pilula ? 600 : 700 }),
        input: (base: any) => ({ ...base, color: texto }),
        container: (base: any) => ({ ...base, color: texto }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base: any, state: any) => ({
            ...base,
            color: hexParaRgba(texto, 0.7),
            transition: 'transform .3s ease',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
            ':hover': { color: texto },
        }),
        clearIndicator: (base: any) => ({ ...base, color: hexParaRgba(texto, 0.6), ':hover': { color: texto } }),
        menu: (base: any) => ({
            ...base,
            backgroundColor: '#ffffff',
            color: texto,
            border: `1px solid ${hexParaRgba(texto, 0.1)}`,
            borderRadius: pilula ? 18 : 10,
            overflow: 'hidden',
            boxShadow: '0 22px 50px rgba(0,0,0,.22)',
            zIndex: 10000,
        }),
        menuList: (base: any) => ({ ...base, padding: 6 }),
        option: (base: any, state: any) => ({
            ...base,
            borderRadius: pilula ? 12 : 6,
            fontSize: 14,
            fontWeight: state.isSelected ? 700 : 500,
            cursor: 'pointer',
            backgroundColor: state.isSelected
                ? hexParaRgba(foco, 0.14)
                : state.isFocused && !celularAP
                ? hexParaRgba(texto, 0.06)
                : 'transparent',
            color: texto,
            ':active': { backgroundColor: hexParaRgba(foco, 0.2) },
        }),
        noOptionsMessage: (base: any) => ({ ...base, color: hexParaRgba(texto, 0.6) }),
        loadingMessage: (base: any) => ({ ...base, color: hexParaRgba(texto, 0.6) }),
    };
};
// Grade de AuroraCard / PanoramaCard com a entrada do modelo (espelho do GradeModeloAP do /imoveis):
// opacity 0 / y 40 → 1 / 0, 0.8s, cascata por coluna, EASE do modelo, MotionConfig "user".
const GradeCondominioAP: React.FC<{ modelo: 'aurora' | 'panorama'; itens: any[]; site: any; codsite: any; className: string }> = ({ modelo, itens, site, codsite, className }) => {
    const reduzido = useMovimentoReduzido();
    const corPreco = site?.cor2 || '#1c2230';
    const corPrecoTexto = ensureReadableTextColor(isColorLight(corPreco) ? site?.cor11 : site?.cor13, corPreco) || '#ffffff';
    return (
        <MotionConfig reducedMotion="user">
            {itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                <motion.div
                    key={slide?.id || i}
                    className={className}
                    initial={reduzido ? false : { opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={reduzido ? { duration: 0 } : { duration: 0.8, delay: (i % 3) * 0.08, ease: modelo === 'aurora' ? EASE_AURORA : EASE_PANORAMA }}
                >
                    {modelo === 'aurora' ? (
                        <AuroraCard slide={slide} site={site} codsite={codsite} favorito={SEM_FAVORITOS} corPreco={corPreco} corPrecoTexto={corPrecoTexto} modoEmpreendimento={modoEmpreendimento} />
                    ) : (
                        <PanoramaCard slide={slide} site={site} codsite={codsite} favorito={SEM_FAVORITOS} reduzido={reduzido} modoEmpreendimento={modoEmpreendimento} />
                    )}
                </motion.div>
            ))}
        </MotionConfig>
    );
};
// ==== fim Aurora+Panorama ====

// ==== Horizonte+Atelier (09/10) ====
// Modelos #8 "Horizonte" (topo 11) e #9 "Atelier" (topo 12): /condominios no visual do /imoveis desses
// modelos. Grade = o MESMO card da listagem (HorizonteCard com card 8 · CardAtelierLista com card 9) em
// modoEmpreendimento; cabeçalho, barra, filtros e vazio vêm das regras [data-modelo='horizonte'] do
// ListPiecesProperties.module.css, do .interna do Atelier e dos blocos "Horizonte+Atelier" dos CSS desta
// página. Fora dos dois: null (zero diff).
import { MotionConfig as MotionConfigHz } from 'framer-motion';
import { isMobile as celularHz } from 'react-device-detect';
import type { IHorizonteCard } from '../../../../../components/carousel/apartment/CardHorizonte';
import {
    EASE_HORIZONTE,
    ehModeloHorizonte,
    hexParaRgba as hexParaRgbaHz,
    useMovimentoReduzido as useMovimentoReduzidoHz,
    varsHorizonteInterna,
} from '../../../../../components/pieces/horizonte/horizonte';
import type { ModoEmpreendimentoCard } from '../../../../../utils/empreendimentoCard';
const HorizonteCard = dynamic<IHorizonteCard>(() => import('../../../../../components/carousel/apartment/CardHorizonte').then((m) => m.HorizonteCard), { ssr: false });
const CardAtelierLista = dynamic<{ slide: any; site: any; codsite?: any; favorito: number[]; modoEmpreendimento?: ModoEmpreendimentoCard }>(
    () => import('../../../../../components/carousel/apartment/CardAtelier').then((m) => m.CardAtelierLista),
    { ssr: false }
);
/** Grade com o card do modelo, como no /imoveis: 'horizonte' (topo 11 + card 8) · 'atelier' (card 9) · null. */
export const cardCondominioHA = (site: any): 'horizonte' | 'atelier' | null =>
    ehModeloHorizonte(site) && Number(site?.modelo_card) === 8 ? 'horizonte' : Number(site?.modelo_card) === 9 ? 'atelier' : null;
/** Cores do Horizonte (as --hz-* da raiz) para os inline do filtro; null fora do topo 11. */
export const coresCondominioHz = (site: any): { texto: string; fundo: string; acento: string } | null => {
    const v = varsHorizonteInterna(site);
    return v ? { texto: v['--hz-texto'], fundo: v['--hz-fundo'], acento: v['--hz-acento'] } : null;
};
/** react-select no idioma do Horizonte — ESPELHO de estilosSelectHorizonte (ListPiecesProperties.tsx). */
export const estilosSelectCondominioHz = ({ texto, fundo, acento }: { texto: string; fundo: string; acento: string }) => ({
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 44,
        borderRadius: 999,
        background: 'transparent',
        boxShadow: 'none',
        border: `1px solid ${state.isFocused ? acento : hexParaRgbaHz(texto, 0.28)}`,
        padding: '0 8px 0 12px',
        cursor: 'pointer',
        transition: 'border-color .2s ease',
        ':hover': { borderColor: state.isFocused ? acento : hexParaRgbaHz(texto, 0.5) },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '2px 4px' }),
    placeholder: (base: any) => ({ ...base, color: hexParaRgbaHz(texto, 0.6), fontSize: 14, fontWeight: 500 }),
    singleValue: (base: any) => ({ ...base, color: texto, fontSize: 14, fontWeight: 600 }),
    input: (base: any) => ({ ...base, color: texto }),
    container: (base: any) => ({ ...base, color: texto }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any, state: any) => ({
        ...base,
        color: hexParaRgbaHz(texto, 0.7),
        transition: 'transform .3s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
        ':hover': { color: texto },
    }),
    clearIndicator: (base: any) => ({ ...base, color: hexParaRgbaHz(texto, 0.6), ':hover': { color: texto } }),
    menu: (base: any) => ({
        ...base,
        backgroundColor: fundo,
        color: texto,
        border: `1px solid ${hexParaRgbaHz(texto, 0.14)}`,
        borderRadius: 18,
        overflow: 'hidden',
        boxShadow: '0 18px 40px -22px rgba(0,0,0,.45)',
        zIndex: 10000,
    }),
    menuList: (base: any) => ({ ...base, padding: 6 }),
    option: (base: any, state: any) => ({
        ...base,
        borderRadius: 12,
        fontSize: 14,
        cursor: 'pointer',
        backgroundColor: state.isSelected ? texto : state.isFocused && !celularHz ? hexParaRgbaHz(texto, 0.08) : 'transparent',
        color: state.isSelected ? fundo : texto,
        ':active': { backgroundColor: hexParaRgbaHz(texto, 0.14) },
    }),
    noOptionsMessage: (base: any) => ({ ...base, color: hexParaRgbaHz(texto, 0.6) }),
    loadingMessage: (base: any) => ({ ...base, color: hexParaRgbaHz(texto, 0.6) }),
});
// Grade de HorizonteCard (ESPELHO do GradeHorizonte do /imoveis): opacity 0 / y 56 → 1 / 0, 0.9s,
// cascata por coluna, EASE_HORIZONTE, MotionConfig "user".
const GradeCondominioHz: React.FC<{ itens: any[]; site: any; codsite: any; className: string }> = ({ itens, site, codsite, className }) => {
    const reduzido = useMovimentoReduzidoHz();
    return (
        <MotionConfigHz reducedMotion="user">
            {itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                <motion.div
                    key={slide?.id || i}
                    className={className}
                    initial={reduzido ? false : { opacity: 0, y: 56 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={reduzido ? { duration: 0 } : { duration: 0.9, delay: (i % 3) * 0.09, ease: EASE_HORIZONTE }}
                >
                    <HorizonteCard slide={slide} site={site} codsite={codsite} favorito={SEM_FAVORITOS} reduzido={reduzido} modoEmpreendimento={modoEmpreendimento} />
                </motion.div>
            ))}
        </MotionConfigHz>
    );
};
// ==== fim Horizonte+Atelier ====

// ==== Larissa (13/10) ====
// Modelo #11 "Larissa" (topo 14): /condominios no visual da home do modelo — GRADE com o LarissaCard (card 11) em
// modoEmpreendimento (.grade_lr/.card_lr do ListPiecesCondominio.module.css); cabeçalho, chips, barra, filtros e
// vazio = regras [data-modelo='larissa'] do Company.module.css sobre as vars --lr-* da raiz (varsLarissaInterna).
// O que precisa de cor em JS (react-select, spinner, inline do cabeçalho) lê as MESMAS vars via coresCondominioLr.
// Fora do topo 14: null/false (zero diff).
import { MotionConfig as MotionConfigLr } from 'framer-motion';
import { isMobile as celularLr } from 'react-device-detect';
import type { ILarissaCard } from '../../../../../components/carousel/apartment/CardLarissa';
import {
    EASE_LARISSA,
    ehModeloLarissa,
    hexParaRgba as hexParaRgbaLr,
    paletaLarissa,
    useMovimentoReduzido as useMovimentoReduzidoLr,
    varsLarissaInterna,
} from '../../../../../components/pieces/larissa/larissa';
const LarissaCard = dynamic<ILarissaCard>(() => import('../../../../../components/carousel/apartment/CardLarissa').then((m) => m.LarissaCard), { ssr: false });
/** Grade com o card do modelo, como no /imoveis: topo 14 + card 11. */
export const cardCondominioLr = (site: any): boolean => ehModeloLarissa(site) && Number(site?.modelo_card) === 11;
/** Cores do Larissa (as --lr-* da raiz: texto legível sobre a cor5, fundo da página, base sólida); null fora do topo 14. */
export const coresCondominioLr = (site: any): { texto: string; fundo: string; base: string } | null => {
    const v = varsLarissaInterna(site) as Record<string, string> | null;
    return v ? { texto: v['--lr-texto'], fundo: v['--lr-fundo-pagina'], base: paletaLarissa(site).base } : null;
};
/** react-select no idioma do Larissa: 44px raio 8, linha fina, menu raio 12, opção escolhida sólida na tinta. */
export const estilosSelectCondominioLr = ({ texto, fundo }: { texto: string; fundo: string }) => ({
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 44,
        borderRadius: 8,
        background: 'transparent',
        boxShadow: 'none',
        border: `1px solid ${state.isFocused ? texto : hexParaRgbaLr(texto, 0.24)}`,
        padding: '0 6px 0 8px',
        cursor: 'pointer',
        transition: 'border-color .2s ease',
        ':hover': { borderColor: state.isFocused ? texto : hexParaRgbaLr(texto, 0.5) },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '2px 4px' }),
    placeholder: (base: any) => ({ ...base, color: hexParaRgbaLr(texto, 0.62), fontSize: 14, fontWeight: 400 }),
    singleValue: (base: any) => ({ ...base, color: texto, fontSize: 14, fontWeight: 500 }),
    input: (base: any) => ({ ...base, color: texto }),
    container: (base: any) => ({ ...base, color: texto }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any, state: any) => ({
        ...base,
        color: hexParaRgbaLr(texto, 0.7),
        transition: 'transform .3s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
        ':hover': { color: texto },
    }),
    clearIndicator: (base: any) => ({ ...base, color: hexParaRgbaLr(texto, 0.6), ':hover': { color: texto } }),
    menu: (base: any) => ({
        ...base,
        backgroundColor: fundo,
        color: texto,
        border: `1px solid ${hexParaRgbaLr(texto, 0.14)}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 20px 40px -12px rgba(0,0,0,.25)',
        zIndex: 10000,
    }),
    menuList: (base: any) => ({ ...base, padding: 6 }),
    option: (base: any, state: any) => ({
        ...base,
        borderRadius: 8,
        fontSize: 14,
        fontWeight: state.isSelected ? 500 : 400,
        cursor: 'pointer',
        backgroundColor: state.isSelected ? texto : state.isFocused && !celularLr ? hexParaRgbaLr(texto, 0.08) : 'transparent',
        color: state.isSelected ? fundo : texto,
        ':active': { backgroundColor: hexParaRgbaLr(texto, 0.14) },
    }),
    noOptionsMessage: (base: any) => ({ ...base, color: hexParaRgbaLr(texto, 0.6) }),
    loadingMessage: (base: any) => ({ ...base, color: hexParaRgbaLr(texto, 0.6) }),
});
// Grade de LarissaCard com a entrada da vitrine da home (CardLarissa.tsx): opacity 0 / y 28 → 1 / 0, 0.7s,
// cascata por coluna, EASE_LARISSA, MotionConfig "user".
const GradeCondominioLr: React.FC<{ itens: any[]; site: any; codsite: any; className: string }> = ({ itens, site, codsite, className }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfigLr reducedMotion="user">
            {itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                <motion.div
                    key={slide?.id || i}
                    className={className}
                    initial={reduzido ? false : { opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={reduzido ? { duration: 0 } : { duration: 0.7, delay: (i % 4) * 0.08, ease: EASE_LARISSA }}
                >
                    <LarissaCard slide={slide} site={site} codsite={codsite} favorito={SEM_FAVORITOS} modoEmpreendimento={modoEmpreendimento} />
                </motion.div>
            ))}
        </MotionConfigLr>
    );
};
// ==== fim Larissa ====
// ==== Metrópole (21/10) ====
// Modelo #12 "Metrópole" (topo 15): /condominios no visual do modelo — GRADE com o MetropoleCard (card 12) em
// modoEmpreendimento (.grade_mt/.card_mt do ListPiecesCondominio.module.css); cabeçalho = regras
// [data-modelo='metropole'] do Company.module.css; chips, barra, filtros e vazio = as do ListPiecesProperties.module.css
// (mesmo atributo na raiz), sobre as vars --mt-* da raiz (varsMetropoleInterna). O que precisa de cor em JS (react-select,
// spinner, slider) lê a mesma paleta via coresCondominioMt. Fora do topo 15: null/false (zero diff).
import type { IMetropoleCard } from '../../../../../components/carousel/apartment/CardMetropole';
import {
    EASE_METROPOLE,
    ehModeloMetropole,
    paletaMetropole,
    useMovimentoReduzido as useMovimentoReduzidoMt,
    varsMetropoleInterna,
} from '../../../../../components/pieces/metropole/metropole';
const MetropoleCard = dynamic<IMetropoleCard>(() => import('../../../../../components/carousel/apartment/CardMetropole').then((m) => m.MetropoleCard), { ssr: false });
/** Grade com o card do modelo, como no /imoveis: topo 15 + card 12. */
export const cardCondominioMt = (site: any): boolean => ehModeloMetropole(site) && Number(site?.modelo_card) === 12;
/** Cores do Metrópole: texto legível sobre a cor5 (= --mt-texto-pagina da raiz) + paleta do modelo (painel de filtros BRANCO); null fora do topo 15. */
export type CoresCondominioMt = { texto: string; acento: string; acentoSuave: string; tinta: string; cinza: string; borda: string; fundo: string };
export const coresCondominioMt = (site: any): CoresCondominioMt | null => {
    if (!ehModeloMetropole(site)) return null;
    const p = paletaMetropole(site);
    const vars = (varsMetropoleInterna(site) || {}) as unknown as Record<string, string>;
    return {
        texto: vars['--mt-texto-pagina'] || p.tinta,
        acento: p.acento,
        acentoSuave: p.acentoSuave,
        tinta: p.tinta,
        cinza: p.cinza,
        borda: p.borda,
        fundo: p.fundo,
    };
};
/** react-select no idioma do Metrópole (painel branco): 44px raio 8, borda fina, foco no acento; menu branco raio 8. */
export const estilosSelectCondominioMt = (c: CoresCondominioMt) => ({
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 44,
        borderRadius: 8,
        background: '#ffffff',
        boxShadow: 'none',
        border: `1px solid ${state.isFocused ? c.acento : c.borda}`,
        padding: '0 4px 0 6px',
        cursor: 'pointer',
        transition: 'border-color .2s ease, background-color .2s ease',
        ':hover': { borderColor: c.acento },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '2px 6px' }),
    placeholder: (base: any) => ({ ...base, color: c.cinza, fontSize: 14, fontWeight: 400 }),
    singleValue: (base: any) => ({ ...base, color: c.tinta, fontSize: 14, fontWeight: 600 }),
    input: (base: any) => ({ ...base, color: c.tinta }),
    container: (base: any) => ({ ...base, color: c.tinta }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any, state: any) => ({
        ...base,
        color: c.cinza,
        transition: 'transform .3s ease, color .2s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
        ':hover': { color: c.acento },
    }),
    clearIndicator: (base: any) => ({ ...base, color: c.cinza, ':hover': { color: c.acento } }),
    menu: (base: any) => ({
        ...base,
        backgroundColor: '#ffffff',
        color: c.tinta,
        border: `1px solid ${c.borda}`,
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(42, 42, 42, 0.16)',
        zIndex: 10000,
    }),
    menuList: (base: any) => ({ ...base, padding: 6 }),
    option: (base: any, state: any) => ({
        ...base,
        borderRadius: 6,
        fontSize: 14,
        fontWeight: state.isSelected ? 700 : 400,
        cursor: 'pointer',
        backgroundColor: state.isSelected ? c.acentoSuave : state.isFocused && !celularLr ? c.fundo : 'transparent',
        color: state.isSelected ? c.acento : c.tinta,
        ':active': { backgroundColor: c.acentoSuave },
    }),
    noOptionsMessage: (base: any) => ({ ...base, color: c.cinza }),
    loadingMessage: (base: any) => ({ ...base, color: c.cinza }),
});
// Grade de MetropoleCard com a entrada do modelo (opacity 0 / y 24 → 1 / 0, 0.7s, cascata por coluna, EASE_METROPOLE —
// a mesma dos cards da home e do /imoveis), MotionConfig "user".
const GradeCondominioMt: React.FC<{ itens: any[]; site: any; codsite: any; className: string }> = ({ itens, site, codsite, className }) => {
    const reduzido = useMovimentoReduzidoMt();
    return (
        <MotionConfigLr reducedMotion="user">
            {itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                <motion.div
                    key={slide?.id || i}
                    className={className}
                    initial={reduzido ? false : { opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={reduzido ? { duration: 0 } : { duration: 0.7, delay: (i % 3) * 0.08, ease: EASE_METROPOLE }}
                >
                    <MetropoleCard slide={slide} site={site} codsite={codsite} favorito={SEM_FAVORITOS} modoEmpreendimento={modoEmpreendimento} />
                </motion.div>
            ))}
        </MotionConfigLr>
    );
};
// ==== fim Metrópole ====
// ==== Internacional (21/10) ====
// Modelo #12 "Internacional" (topo 16): /condominios no visual do modelo — GRADE com o InternacionalCard (card 13) em
// modoEmpreendimento (.grade_in/.card_in do ListPiecesCondominio.module.css); cabeçalho, chips, barra, filtros e vazio =
// regras [data-modelo='internacional'] do Company.module.css sobre as vars --in-* da raiz (varsInternacionalPagina).
// Fora do topo 16: null/false (zero diff).
import type { IInternacionalCard } from '../../../../../components/carousel/apartment/CardInternacional';
import { EASE_INTERNACIONAL, ehModeloInternacional } from '../../../../../components/pieces/internacional/internacional';
import { coresInternas, estilosSelectInternacional } from '../../../../../components/pieces/internacional/internacionalInternas';
import type { CoresInternas } from '../../../../../components/pieces/internacional/internacionalInternas';
const InternacionalCard = dynamic<IInternacionalCard>(() => import('../../../../../components/carousel/apartment/CardInternacional').then((m) => m.InternacionalCard), { ssr: false });
/** Grade com o card do modelo, como no /imoveis: topo 16 + card 13. */
export const cardCondominioIn = (site: any): boolean => ehModeloInternacional(site) && Number(site?.modelo_card) === 13;
/** Cores do Internacional (paleta + tons de página); null fora do topo 16. */
export const coresCondominioIn = (site: any): CoresInternas | null => coresInternas(site);
/** react-select em pílula (painel branco do modelo). */
export const estilosSelectCondominioIn = (c: CoresInternas) => estilosSelectInternacional(c, true);
const GradeCondominioIn: React.FC<{ itens: any[]; site: any; codsite: any; className: string }> = ({ itens, site, codsite, className }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfigLr reducedMotion="user">
            {itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                <motion.div
                    key={slide?.id || i}
                    className={className}
                    initial={reduzido ? false : { opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={reduzido ? { duration: 0 } : { duration: 0.7, delay: (i % 3) * 0.08, ease: EASE_INTERNACIONAL }}
                >
                    <InternacionalCard slide={slide} site={site} codsite={codsite} favorito={SEM_FAVORITOS} modoEmpreendimento={modoEmpreendimento} />
                </motion.div>
            ))}
        </MotionConfigLr>
    );
};
// ==== fim Internacional ====
// ==== Norte (21/10) ====
// Modelo #13 "Norte" (topo 17): /condominios no visual do modelo — GRADE 3/2/1 com o NorteCard (card 14) em
// modoEmpreendimento (o mesmo card branco raio 8 da home); barra/filtros/vazio = regras [data-modelo='norte'] do
// Company.module.css sobre as vars --nt-* da raiz (varsNortePagina). Fora do topo 17: null ⇒ nada muda.
import type { INorteCard } from '../../../../../components/carousel/apartment/CardNorte';
import { coresInternasNorte, estilosSelectNorte } from '../../../../../components/pieces/norte/norteInternas';
import type { CoresInternasNorte } from '../../../../../components/pieces/norte/norteInternas';
const NorteCard = dynamic<INorteCard>(() => import('../../../../../components/carousel/apartment/CardNorte').then((m) => m.NorteCard), { ssr: false });
/** Grade com o card do modelo, como no /imoveis: topo 17 + card 14. */
export const cardCondominioNt = (site: any): boolean => Number(site?.modelo_topo) === 17 && Number(site?.modelo_card) === 14;
/** Cores do Norte (paleta + tons de página); null fora do topo 17. */
export const coresCondominioNt = (site: any): CoresInternasNorte | null => coresInternasNorte(site);
/** react-select RETO (campo branco de 48px do modelo). */
export const estilosSelectCondominioNt = (c: CoresInternasNorte) => estilosSelectNorte(c, true);
const GradeCondominioNt: React.FC<{ itens: any[]; site: any; codsite: any; className: string }> = ({ itens, site, codsite, className }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfigLr reducedMotion="user">
            {itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                <motion.div
                    key={slide?.id || i}
                    className={className}
                    initial={reduzido ? false : { opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={reduzido ? { duration: 0 } : { duration: 0.5, delay: (i % 3) * 0.06, ease: EASE_INTERNACIONAL }}
                >
                    <NorteCard slide={slide} site={site} codsite={codsite} favorito={SEM_FAVORITOS} modoEmpreendimento={modoEmpreendimento} />
                </motion.div>
            ))}
        </MotionConfigLr>
    );
};
// ==== fim Norte ====

// ==== Campo (23/10) ====
// Modelo #14 "Campo" (topo 18): /condominios no visual do modelo — GRADE 3/2/1 com o CampoCard (card 15) em
// modoEmpreendimento (o mesmo cartão raio 20 da home); barra/filtros/vazio = regras [data-modelo='campo'] do
// Company.module.css sobre as vars --cp-* da raiz (varsCampoPagina). Fora do topo 18: null ⇒ nada muda.
import type { ICampoCard } from '../../../../../components/carousel/apartment/CardCampo';
import { coresInternasCampo, estilosSelectCampo } from '../../../../../components/pieces/campo/campoInternas';
import type { CoresInternasCampo } from '../../../../../components/pieces/campo/campoInternas';
const CampoCard = dynamic<ICampoCard>(() => import('../../../../../components/carousel/apartment/CardCampo').then((m) => m.CampoCard), { ssr: false });
/** Grade com o card do modelo, como no /imoveis: topo 18 + card 15. */
export const cardCondominioCp = (site: any): boolean => Number(site?.modelo_topo) === 18 && Number(site?.modelo_card) === 15;
/** Cores do Campo (paleta + tons de página); null fora do topo 18. */
export const coresCondominioCp = (site: any): CoresInternasCampo | null => coresInternasCampo(site);
/** react-select em PÍLULA (campo cartão de 48px do modelo, menu raio 20). */
export const estilosSelectCondominioCp = (c: CoresInternasCampo) => estilosSelectCampo(c, true);
const GradeCondominioCp: React.FC<{ itens: any[]; site: any; codsite: any; className: string }> = ({ itens, site, codsite, className }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfigLr reducedMotion="user">
            {itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                <motion.div
                    key={slide?.id || i}
                    className={className}
                    initial={reduzido ? false : { opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={reduzido ? { duration: 0 } : { duration: 0.5, delay: (i % 3) * 0.06, ease: EASE_INTERNACIONAL }}
                >
                    <CampoCard slide={slide} site={site} codsite={codsite} favorito={SEM_FAVORITOS} modoEmpreendimento={modoEmpreendimento} />
                </motion.div>
            ))}
        </MotionConfigLr>
    );
};
// ==== fim Campo ====

const getCondominioLink =(site: any, nome: string, id?: number, codsite?: any, origin?: any) => {
    if (id && Number(id) > 0) {
        return appendCodsiteQuery(`/empreendimento/${id}`, codsite, origin);
    }

    const transacao = site?.transacoes?.[0]?.toLowerCase() || 'venda';
    const slug = removerAcentos(String(nome || '-'))
        .toLowerCase()
        .replaceAll('/', '-')
        .replaceAll('?', '-')
        .replaceAll('.', '-')
        .replaceAll('\n', '-')
        .replaceAll(' ', '-');

    return appendCodsiteQuery(`/imoveis/${transacao}/-/-/${slug || '-'}/-`, codsite, origin);
};

const ListPiecesCondominio: React.FC<{
    allCompany: any;
    site: any;
    allCities: any;
    origin?: any;
    codsite?: any;
    isHorizontal?: boolean;
    onLoadMore?: () => void;
    hasMore?: boolean;
    loadingMore?: boolean;
    loadingInitial?: boolean;
    temFiltro?: boolean;
    onLimparFiltros?: () => void;
}> = ({
    allCompany,
    site,
    allCities,
    origin,
    codsite,
    isHorizontal,
    onLoadMore,
    hasMore,
    loadingMore,
    loadingInitial,
    temFiltro,
    onLimparFiltros
}) => {
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const loadLockRef = useRef(false);
    // Aurora/Panorama: cores do modelo + grade com o card da home (null/false nos demais sites)
    const coresAP = coresCondominioAP(site);
    const gradeAP = cardCondominioAP(site, coresAP) && !isHorizontal;
    // Horizonte (card 8) / Atelier (card 9): grade com o card do modelo; Horizonte sem os inline legados do vazio
    const gradeHA = cardCondominioHA(site);
    const gradeHz = gradeHA === 'horizonte' && !isHorizontal;
    const semInlineHz = ehModeloHorizonte(site);
    // Larissa (topo 14 + card 11): grade com o LarissaCard; sem os inline legados do vazio (estilo no Company.module.css)
    const coresLr = coresCondominioLr(site);
    const gradeLr = cardCondominioLr(site) && !isHorizontal;
    const semInlineLr = Boolean(coresLr);
    // Metrópole (topo 15 + card 12): idem, grade com o MetropoleCard (estilo no Company/ListPiecesCondominio/ListPiecesProperties .module.css)
    const coresMt = coresCondominioMt(site);
    const gradeMt = cardCondominioMt(site) && !isHorizontal;
    const semInlineMt = Boolean(coresMt);
    // Internacional (topo 16 + card 13): idem, grade com o InternacionalCard
    const coresIn = coresCondominioIn(site);
    const gradeIn = cardCondominioIn(site) && !isHorizontal;
    // Norte (topo 17 + card 14): idem, grade com o NorteCard
    const coresNt = coresCondominioNt(site);
    const gradeNt = cardCondominioNt(site) && !isHorizontal;
    // Campo (topo 18 + card 15): idem, grade com o CampoCard
    const coresCp = coresCondominioCp(site);
    const gradeCp = cardCondominioCp(site) && !isHorizontal;
    // ESPELHO de ListPiecesProperties.tsx (corLetra2 do estado vazio) — manter em sincronia
    const corLetra2 = coresAP ? coresAP.texto : coresLr ? coresLr.texto : coresMt ? coresMt.texto : coresIn ? coresIn.texto : coresNt ? coresNt.texto : coresCp ? coresCp.tinta : isColorLight(site?.cor5) ? (site?.cor11 || '#111827') : (site?.cor13 || '#ffffff');
    // Metrópole: o cartão do estado vazio é BRANCO (como no /imoveis do modelo) — texto na tinta do modelo, não no texto da página (cor5 escura)
    const corTextoVazio = coresMt ? coresMt.tinta : corLetra2;

    // Foto de empreendimento não passa pelo CDN de marca d'água (só imóvel tem): card sem marca.
    const siteCard = useMemo(
        () => ({ ...site, marca_dagua: { ...site?.marca_dagua, ativada: false } }),
        [site]
    );

    const itens = useMemo(() => {
        return (allCompany || []).map((emp: any) => ({
            slide: empreendimentoParaSlide(emp, allCities),
            modoEmpreendimento: {
                href: getCondominioLink(site, emp?.nome, Number(emp?.id), codsite, origin),
                rotulo: rotuloEmpreendimento(emp, allCities),
                precoPrefixo: PRECO_PREFIXO_EMPREENDIMENTO,
                botao: CTA_EMPREENDIMENTO,
            },
        }));
    }, [allCities, allCompany, codsite, origin, site]);

    useEffect(() => {
        if (!loadingMore) loadLockRef.current = false;
    }, [loadingMore]);

    useEffect(() => {
        if (!hasMore || !onLoadMore || loadingMore) return;

        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (!first?.isIntersecting || loadLockRef.current) return;
                loadLockRef.current = true;
                onLoadMore();
            },
            { rootMargin: '700px 0px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [allCompany?.length, hasMore, loadingMore, onLoadMore]);

    const showEmptyState = !loadingInitial && !loadingMore && !hasMore && itens.length === 0;

    return (
        <>
            {!loadingInitial && itens.length > 0 ? (
                <div className={`${styles.container}${gradeAP ? ` ${styles.grade_ap}` : ''}${gradeHz ? ` ${styles.grade_hz}` : ''}${gradeLr ? ` ${condoStyles.grade_lr}` : ''}${gradeMt ? ` ${condoStyles.grade_mt}` : ''}${gradeIn ? ` ${condoStyles.grade_in}` : ''}${gradeNt ? ` ${condoStyles.grade_nt}` : ''}${gradeCp ? ` ${condoStyles.grade_cp}` : ''}`} data-grade-modelo={gradeAP ? coresAP?.modelo : gradeMt ? 'metropole' : gradeIn ? 'internacional' : gradeNt ? 'norte' : gradeCp ? 'campo' : undefined}>
                    {/* ESPELHO de ListPiecesProperties.tsx (switch de card por modelo_card) — manter em sincronia */}
                    { isHorizontal ?
                        itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                            <motion.div
                                key={slide?.id || i}
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: (i % 12) * 0.2 }}
                                className={styles.card_horizontal1}
                            >
                                <CardApartamentHorizontal
                                    slide={slide}
                                    i={i}
                                    site={siteCard}
                                    stylesCard={styles.card_horizontal}
                                    allViewProperties={SEM_VISUALIZADOS}
                                    favorito={SEM_FAVORITOS}
                                    setFavorito={semSetFavorito}
                                    modoEmpreendimento={modoEmpreendimento}
                                />
                            </motion.div>
                        ))
                    : gradeAP && coresAP ?
                        <GradeCondominioAP modelo={coresAP.modelo} itens={itens} site={siteCard} codsite={codsite} className={styles.card_ap} />
                    : gradeHz ?
                        <GradeCondominioHz itens={itens} site={siteCard} codsite={codsite} className={styles.card_hz} />
                    : gradeLr ?
                        <GradeCondominioLr itens={itens} site={siteCard} codsite={codsite} className={condoStyles.card_lr} />
                    : gradeMt ?
                        <GradeCondominioMt itens={itens} site={siteCard} codsite={codsite} className={condoStyles.card_mt} />
                    : gradeIn ?
                        <GradeCondominioIn itens={itens} site={siteCard} codsite={codsite} className={condoStyles.card_in} />
                    : gradeNt ?
                        <GradeCondominioNt itens={itens} site={siteCard} codsite={codsite} className={condoStyles.card_nt} />
                    : gradeCp ?
                        <GradeCondominioCp itens={itens} site={siteCard} codsite={codsite} className={condoStyles.card_cp} />
                    :
                        itens.map(({ slide, modoEmpreendimento }: any, i: number) => (
                            <motion.div
                                key={slide?.id || i}
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: (i % 12) * 0.2 }}
                                className={site?.modelo_card > 1 ? styles.card11 : styles.card1}
                            >
                                {gradeHA === 'atelier' ?
                                <CardAtelierLista
                                    slide={slide}
                                    site={siteCard}
                                    codsite={codsite}
                                    favorito={SEM_FAVORITOS}
                                    modoEmpreendimento={modoEmpreendimento}
                                /> : site?.modelo_card == 4 ?
                                <CardApartamentImersivo
                                    slide={slide}
                                    i={i}
                                    site={siteCard}
                                    allViewProperties={SEM_VISUALIZADOS}
                                    favorito={SEM_FAVORITOS}
                                    codsite={codsite}
                                    modoEmpreendimento={modoEmpreendimento}
                                /> : site?.modelo_card == 2 ?
                                <CardApartament1
                                    slide={slide}
                                    i={i}
                                    site={siteCard}
                                    stylesCard={styles.card3}
                                    allViewProperties={SEM_VISUALIZADOS}
                                    favorito={SEM_FAVORITOS}
                                    codsite={codsite}
                                    modoEmpreendimento={modoEmpreendimento}
                                /> : site?.modelo_card == 1 ?
                                <CardApartament
                                    slide={slide}
                                    i={i}
                                    site={siteCard}
                                    stylesCard={styles.card2}
                                    allViewProperties={SEM_VISUALIZADOS}
                                    favorito={SEM_FAVORITOS}
                                    codsite={codsite}
                                    modoEmpreendimento={modoEmpreendimento}
                                /> :
                                <CardApartament3
                                    slide={slide}
                                    i={i}
                                    site={siteCard}
                                    stylesCard={styles.card2}
                                    allViewProperties={SEM_VISUALIZADOS}
                                    favorito={SEM_FAVORITOS}
                                    codsite={codsite}
                                    modoEmpreendimento={modoEmpreendimento}
                                />
                                }
                            </motion.div>
                        ))
                    }
                    {/* Paginação DENTRO do .container (flex-wrap, width 100% = linha própria): fora dele viravam
                        itens do .container_all_list (flex em linha) — a frase de fim ficava espremida à direita
                        dos cards e o sentinela, no topo, disparava o carregamento de todas as páginas de uma vez. */}
                    {loadingMore ? (
                        <div className={condoStyles.loadingMessage}>Buscando mais resultados...</div>
                    ) : null}
                    {hasMore ? (
                        <div ref={sentinelRef} className={condoStyles.scrollSentinel} />
                    ) : null}
                    {!hasMore ? (
                        <div className={condoStyles.endMessage} style={{ color: corLetra2 }}>Você chegou ao fim da lista.</div>
                    ) : null}
                </div>
            ) : null}
            {showEmptyState ? (
                <div className={styles.empty_state}>
                    {/* ESPELHO de ListPiecesProperties.tsx (estado vazio e suas cores) — manter em sincronia */}
                    <div
                        className={styles.empty_card}
                        style={coresAP || semInlineHz || semInlineLr || semInlineMt ? undefined : { background: hexToRgba(site?.cor2, 0.06), borderColor: hexToRgba(site?.cor2, 0.22) }}
                    >
                        <div className={styles.empty_icon} style={coresAP || semInlineHz || semInlineLr || semInlineMt ? undefined : { background: hexToRgba(site?.cor2, 0.12), color: site?.cor2 }}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="11" cy="11" r="7" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                <line x1="8" y1="11" x2="14" y2="11" />
                            </svg>
                        </div>
                        <p className={styles.empty_title} style={{ color: corTextoVazio }}>
                            Nenhum empreendimento encontrado
                        </p>
                        <p className={styles.empty_subtitle} style={{ color: hexToRgba(corTextoVazio, 0.72) }}>
                            {temFiltro
                                ? 'Não encontramos empreendimentos para os filtros selecionados. Tente ajustar a busca.'
                                : 'Ainda não há empreendimentos cadastrados para esta página.'}
                        </p>
                        {temFiltro && onLimparFiltros ? (
                            <button
                                aria-label="Botão para limpar os filtros"
                                title="Clique para limpar os filtros"
                                type="button"
                                onClick={onLimparFiltros}
                                className={styles.ver_todos_btn}
                                style={coresAP || semInlineHz || semInlineLr || semInlineMt ? undefined : {
                                    backgroundColor: site?.cor2,
                                    color: isColorLight(site?.cor2) ? site?.cor11 : site?.cor13,
                                }}
                            >
                                Limpar filtros
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : loadingInitial ? (
                <div className={styles.adjust_loading}>
                    <TailSpin
                        color={coresAP ? coresAP.acento : semInlineHz ? coresCondominioHz(site)?.acento : coresLr ? coresLr.base : coresMt ? coresMt.acento : coresIn ? coresIn.base : coresNt ? coresNt.base : coresCp ? coresCp.base : "rgb(195, 167, 96)"}
                        height={80}
                        width={80}
                    />
                </div>
            ) : null}

        </>
    );
};

export default ListPiecesCondominio;

export const getServerSideProps = async () => ({ notFound: true });
